import type { Letter } from "../types";

/**
 * The full Devanagari inventory: vowels, consonants, matras, signs, numerals,
 * nukta letters, and common conjuncts.
 *
 * Invariants (enforced by tests):
 * - Every `glyph` is a single NFC-normalized Devanagari sequence.
 * - `audioText` mirrors `glyph`.
 * - `exampleWordIds` is empty for now (filled in Task 5).
 * - `confusableIds` are symmetric and only reference ids defined below.
 */

const vowels: Letter[] = [
  {
    id: "vowel-a",
    glyph: "अ",
    iast: "a",
    hint: "a — short 'a', as the 'u' in 'but'",
    kind: "vowel",
    audioText: "अ",
    exampleWordIds: ["w-ang", "w-ank"],
    confusableIds: [],
  },
  {
    id: "vowel-aa",
    glyph: "आ",
    iast: "ā",
    hint: "aa — long 'a', as in 'father'",
    kind: "vowel",
    audioText: "आ",
    exampleWordIds: ["w-aag", "w-aaj"],
    confusableIds: [],
  },
  {
    id: "vowel-i",
    glyph: "इ",
    iast: "i",
    hint: "i — short 'i', as in 'bit'",
    kind: "vowel",
    audioText: "इ",
    exampleWordIds: ["w-imli"],
    confusableIds: [],
  },
  {
    id: "vowel-ii",
    glyph: "ई",
    iast: "ī",
    hint: "ii — long 'ee', as in 'seed'",
    kind: "vowel",
    audioText: "ई",
    exampleWordIds: ["w-bhai"],
    confusableIds: [],
  },
  {
    id: "vowel-u",
    glyph: "उ",
    iast: "u",
    hint: "u — short 'oo', as in 'put'",
    kind: "vowel",
    audioText: "उ",
    exampleWordIds: ["w-ug"],
    confusableIds: [],
  },
  {
    id: "vowel-uu",
    glyph: "ऊ",
    iast: "ū",
    hint: "uu — long 'oo', as in 'boot'",
    kind: "vowel",
    audioText: "ऊ",
    exampleWordIds: ["w-oon"],
    confusableIds: [],
  },
  {
    id: "vowel-e",
    glyph: "ए",
    iast: "e",
    hint: "e — long 'ay', as in 'may'",
    kind: "vowel",
    audioText: "ए",
    exampleWordIds: ["w-ek"],
    confusableIds: [],
  },
  {
    id: "vowel-ai",
    glyph: "ऐ",
    iast: "ai",
    hint: "ai — open 'e', as in 'aisle' softened toward 'end'",
    kind: "vowel",
    audioText: "ऐ",
    exampleWordIds: ["w-ainak"],
    confusableIds: [],
  },
  {
    id: "vowel-o",
    glyph: "ओ",
    iast: "o",
    hint: "o — long 'o', as in 'go'",
    kind: "vowel",
    audioText: "ओ",
    exampleWordIds: ["w-os"],
    confusableIds: [],
  },
  {
    id: "vowel-au",
    glyph: "औ",
    iast: "au",
    hint: "au — open 'ou', as in 'ought'",
    kind: "vowel",
    audioText: "औ",
    exampleWordIds: ["w-aurat"],
    confusableIds: [],
  },
  {
    id: "vowel-ri",
    glyph: "ऋ",
    iast: "ṛ",
    hint: "ri — vocalic 'r', like the 'ri' in 'krishna'",
    kind: "vowel",
    audioText: "ऋ",
    exampleWordIds: ["w-rishi"],
    confusableIds: [],
  },
];

const consonants: Letter[] = [
  // ka-varga (velars)
  {
    id: "ka",
    glyph: "क",
    iast: "ka",
    hint: "k — unaspirated, as in 'skate'",
    kind: "consonant",
    varga: "ka-varga",
    audioText: "क",
    exampleWordIds: ["w-ek", "w-ank"],
    confusableIds: [],
  },
  {
    id: "kha",
    glyph: "ख",
    iast: "kha",
    hint: "kh — aspirated k, as in 'khaana'",
    kind: "consonant",
    varga: "ka-varga",
    audioText: "ख",
    exampleWordIds: ["w-khag", "w-cakh"],
    confusableIds: [],
  },
  {
    id: "ga",
    glyph: "ग",
    iast: "ga",
    hint: "g — unaspirated, as in 'go'",
    kind: "consonant",
    varga: "ka-varga",
    audioText: "ग",
    exampleWordIds: ["w-aag", "w-ug"],
    confusableIds: [],
  },
  {
    id: "gha",
    glyph: "घ",
    iast: "gha",
    hint: "gh — aspirated g, breathy, as in 'ghar'",
    kind: "consonant",
    varga: "ka-varga",
    audioText: "घ",
    exampleWordIds: ["w-ghat", "w-ghan"],
    confusableIds: ["dha"],
  },
  {
    id: "nga",
    glyph: "ङ",
    iast: "ṅa",
    hint: "ng — nasal, as the 'ng' in 'sing'",
    kind: "consonant",
    varga: "ka-varga",
    audioText: "ङ",
    exampleWordIds: ["w-ank-conj"],
    confusableIds: ["dda"],
  },
  // ca-varga (palatals)
  {
    id: "ca",
    glyph: "च",
    iast: "ca",
    hint: "ch — unaspirated, as in 'chai'",
    kind: "consonant",
    varga: "ca-varga",
    audioText: "च",
    exampleWordIds: ["w-cakh", "w-chamak"],
    confusableIds: [],
  },
  {
    id: "cha",
    glyph: "छ",
    iast: "cha",
    hint: "chh — aspirated ch, as in 'chhata'",
    kind: "consonant",
    varga: "ca-varga",
    audioText: "छ",
    exampleWordIds: ["w-chhah", "w-chhatra"],
    confusableIds: [],
  },
  {
    id: "ja",
    glyph: "ज",
    iast: "ja",
    hint: "j — unaspirated, as in 'jam'",
    kind: "consonant",
    varga: "ca-varga",
    audioText: "ज",
    exampleWordIds: ["w-jag", "w-gaj"],
    confusableIds: [],
  },
  {
    id: "jha",
    glyph: "झ",
    iast: "jha",
    hint: "jh — aspirated j, breathy, as in 'jhaanjar'",
    kind: "consonant",
    varga: "ca-varga",
    audioText: "झ",
    exampleWordIds: ["w-jhatak"],
    confusableIds: [],
  },
  {
    id: "nya",
    glyph: "ञ",
    iast: "ña",
    hint: "ny — palatal nasal, as the 'ny' in 'canyon'",
    kind: "consonant",
    varga: "ca-varga",
    audioText: "ञ",
    exampleWordIds: ["w-gyan"],
    confusableIds: [],
  },
  // ta-varga retroflex
  {
    id: "tta",
    glyph: "ट",
    iast: "ṭa",
    hint: "t — retroflex, tongue curled back, hard 't'",
    kind: "consonant",
    varga: "ta-varga-retroflex",
    audioText: "ट",
    exampleWordIds: ["w-ghat", "w-atak"],
    confusableIds: ["ttha"],
  },
  {
    id: "ttha",
    glyph: "ठ",
    iast: "ṭha",
    hint: "th — aspirated retroflex t, tongue curled back",
    kind: "consonant",
    varga: "ta-varga-retroflex",
    audioText: "ठ",
    exampleWordIds: ["w-thag", "w-aath"],
    confusableIds: ["tta"],
  },
  {
    id: "dda",
    glyph: "ड",
    iast: "ḍa",
    hint: "d — retroflex, tongue curled back, hard 'd'",
    kind: "consonant",
    varga: "ta-varga-retroflex",
    audioText: "ड",
    exampleWordIds: ["w-dank", "w-sadak"],
    confusableIds: ["nga", "ddha"],
  },
  {
    id: "ddha",
    glyph: "ढ",
    iast: "ḍha",
    hint: "dh — aspirated retroflex d, tongue curled back",
    kind: "consonant",
    varga: "ta-varga-retroflex",
    audioText: "ढ",
    exampleWordIds: ["w-padhna"],
    confusableIds: ["dda"],
  },
  {
    id: "nna",
    glyph: "ण",
    iast: "ṇa",
    hint: "n — retroflex nasal, tongue curled back",
    kind: "consonant",
    varga: "ta-varga-retroflex",
    audioText: "ण",
    exampleWordIds: ["w-kan"],
    confusableIds: [],
  },
  // ta-varga dental
  {
    id: "ta",
    glyph: "त",
    iast: "ta",
    hint: "t — dental, soft 't', tongue on teeth",
    kind: "consonant",
    varga: "ta-varga-dental",
    audioText: "त",
    exampleWordIds: ["w-tan", "w-aurat"],
    confusableIds: ["na"],
  },
  {
    id: "tha",
    glyph: "थ",
    iast: "tha",
    hint: "th — aspirated dental t, tongue on teeth",
    kind: "consonant",
    varga: "ta-varga-dental",
    audioText: "थ",
    exampleWordIds: ["w-nath", "w-path"],
    confusableIds: ["ya"],
  },
  {
    id: "da",
    glyph: "द",
    iast: "da",
    hint: "d — dental, soft 'd', tongue on teeth",
    kind: "consonant",
    varga: "ta-varga-dental",
    audioText: "द",
    exampleWordIds: ["w-das", "w-do"],
    confusableIds: [],
  },
  {
    id: "dha",
    glyph: "ध",
    iast: "dha",
    hint: "dh — aspirated dental d, breathy, tongue on teeth",
    kind: "consonant",
    varga: "ta-varga-dental",
    audioText: "ध",
    exampleWordIds: ["w-dhan", "w-doodh"],
    confusableIds: ["gha"],
  },
  {
    id: "na",
    glyph: "न",
    iast: "na",
    hint: "n — dental, as in 'nut'",
    kind: "consonant",
    varga: "ta-varga-dental",
    audioText: "न",
    exampleWordIds: ["w-tan", "w-dhan"],
    confusableIds: ["ta"],
  },
  // pa-varga (labials)
  {
    id: "pa",
    glyph: "प",
    iast: "pa",
    hint: "p — unaspirated, as in 'spin'",
    kind: "consonant",
    varga: "pa-varga",
    audioText: "प",
    exampleWordIds: ["w-path", "w-pair"],
    confusableIds: ["ssa"],
  },
  {
    id: "pha",
    glyph: "फ",
    iast: "pha",
    hint: "ph — aspirated p, as in 'phal'",
    kind: "consonant",
    varga: "pa-varga",
    audioText: "फ",
    exampleWordIds: ["w-phal", "w-phul"],
    confusableIds: [],
  },
  {
    id: "ba",
    glyph: "ब",
    iast: "ba",
    hint: "b — unaspirated, as in 'bat'",
    kind: "consonant",
    varga: "pa-varga",
    audioText: "ब",
    exampleWordIds: ["w-sab", "w-bas"],
    confusableIds: ["va"],
  },
  {
    id: "bha",
    glyph: "भ",
    iast: "bha",
    hint: "bh — aspirated b, breathy, as in 'bhaai'",
    kind: "consonant",
    varga: "pa-varga",
    audioText: "भ",
    exampleWordIds: ["w-bhajan", "w-bhai"],
    confusableIds: ["ma"],
  },
  {
    id: "ma",
    glyph: "म",
    iast: "ma",
    hint: "m — as in 'man'",
    kind: "consonant",
    varga: "pa-varga",
    audioText: "म",
    exampleWordIds: ["w-man", "w-aam"],
    confusableIds: ["bha"],
  },
  // antastha (semivowels)
  {
    id: "ya",
    glyph: "य",
    iast: "ya",
    hint: "y — as in 'yes'",
    kind: "consonant",
    varga: "antastha",
    audioText: "य",
    exampleWordIds: ["w-satya", "w-pyar"],
    confusableIds: ["tha"],
  },
  {
    id: "ra",
    glyph: "र",
    iast: "ra",
    hint: "r — lightly tapped, as in Spanish 'pero'",
    kind: "consonant",
    varga: "antastha",
    audioText: "र",
    exampleWordIds: ["w-ghar", "w-garam"],
    confusableIds: [],
  },
  {
    id: "la",
    glyph: "ल",
    iast: "la",
    hint: "l — as in 'love'",
    kind: "consonant",
    varga: "antastha",
    audioText: "ल",
    exampleWordIds: ["w-jal", "w-phal"],
    confusableIds: [],
  },
  {
    id: "va",
    glyph: "व",
    iast: "va",
    hint: "v — soft, between English 'v' and 'w'",
    kind: "consonant",
    varga: "antastha",
    audioText: "व",
    exampleWordIds: ["w-hawa", "w-gaon"],
    confusableIds: ["ba"],
  },
  // ushma (sibilants + ha)
  {
    id: "sha",
    glyph: "श",
    iast: "śa",
    hint: "sh — palatal, as in 'shoe'",
    kind: "consonant",
    varga: "ushma",
    audioText: "श",
    exampleWordIds: ["w-shahar", "w-shaam"],
    confusableIds: [],
  },
  {
    id: "ssa",
    glyph: "ष",
    iast: "ṣa",
    hint: "sh — retroflex, tongue curled back",
    kind: "consonant",
    varga: "ushma",
    audioText: "ष",
    exampleWordIds: ["w-rishi", "w-kshama"],
    confusableIds: ["pa"],
  },
  {
    id: "sa",
    glyph: "स",
    iast: "sa",
    hint: "s — dental, as in 'sun'",
    kind: "consonant",
    varga: "ushma",
    audioText: "स",
    exampleWordIds: ["w-sab", "w-bas"],
    confusableIds: [],
  },
  {
    id: "ha",
    glyph: "ह",
    iast: "ha",
    hint: "h — breathy, as in 'hat'",
    kind: "consonant",
    varga: "ushma",
    audioText: "ह",
    exampleWordIds: ["w-hum", "w-hal"],
    confusableIds: [],
  },
];

const matras: Letter[] = [
  {
    id: "matra-aa",
    glyph: "ा",
    iast: "ā",
    hint: "aa-matra — long 'a' vowel sign, added after a consonant",
    kind: "matra",
    audioText: "ा",
    exampleWordIds: ["w-naam", "w-kaam"],
    confusableIds: [],
  },
  {
    id: "matra-i",
    glyph: "ि",
    iast: "i",
    hint: "i-matra — short 'i' sign, written before the consonant",
    kind: "matra",
    audioText: "ि",
    exampleWordIds: ["w-din", "w-rishi"],
    confusableIds: [],
  },
  {
    id: "matra-ii",
    glyph: "ी",
    iast: "ī",
    hint: "ii-matra — long 'ee' sign, written after the consonant",
    kind: "matra",
    audioText: "ी",
    exampleWordIds: ["w-teen", "w-paani"],
    confusableIds: [],
  },
  {
    id: "matra-u",
    glyph: "ु",
    iast: "u",
    hint: "u-matra — short 'oo' sign, hooked below the consonant",
    kind: "matra",
    audioText: "ु",
    exampleWordIds: ["w-guru", "w-subah"],
    confusableIds: [],
  },
  {
    id: "matra-uu",
    glyph: "ू",
    iast: "ū",
    hint: "uu-matra — long 'oo' sign, hooked below the consonant",
    kind: "matra",
    audioText: "ू",
    exampleWordIds: ["w-phul", "w-doodh"],
    confusableIds: [],
  },
  {
    id: "matra-ri",
    glyph: "ृ",
    iast: "ṛ",
    hint: "ri-matra — vocalic 'r' sign, hooked below the consonant",
    kind: "matra",
    audioText: "ृ",
    exampleWordIds: ["w-kripa"],
    confusableIds: [],
  },
  {
    id: "matra-e",
    glyph: "े",
    iast: "e",
    hint: "e-matra — 'ay' sign, a stroke above the consonant",
    kind: "matra",
    audioText: "े",
    exampleWordIds: ["w-mez", "w-desh"],
    confusableIds: [],
  },
  {
    id: "matra-ai",
    glyph: "ै",
    iast: "ai",
    hint: "ai-matra — open 'e' sign, two strokes above the consonant",
    kind: "matra",
    audioText: "ै",
    exampleWordIds: ["w-pair", "w-paisa"],
    confusableIds: [],
  },
  {
    id: "matra-o",
    glyph: "ो",
    iast: "o",
    hint: "o-matra — long 'o' sign, combining aa- and e-signs",
    kind: "matra",
    audioText: "ो",
    exampleWordIds: ["w-do", "w-log"],
    confusableIds: [],
  },
  {
    id: "matra-au",
    glyph: "ौ",
    iast: "au",
    hint: "au-matra — open 'ou' sign, aa-sign with two strokes",
    kind: "matra",
    audioText: "ौ",
    exampleWordIds: ["w-kaun", "w-mausam"],
    confusableIds: [],
  },
];

const signs: Letter[] = [
  {
    id: "sign-anusvara",
    glyph: "ं",
    iast: "ṃ",
    hint: "anusvara — nasalizes the preceding vowel (dot above)",
    kind: "sign",
    audioText: "ं",
    exampleWordIds: [],
    confusableIds: [],
  },
  {
    id: "sign-chandrabindu",
    glyph: "ँ",
    iast: "m̐",
    hint: "chandrabindu — light nasalization (crescent and dot above)",
    kind: "sign",
    audioText: "ँ",
    exampleWordIds: [],
    confusableIds: [],
  },
  {
    id: "sign-visarga",
    glyph: "ः",
    iast: "ḥ",
    hint: "visarga — a soft 'h' breath after the vowel (two dots)",
    kind: "sign",
    audioText: "ः",
    exampleWordIds: [],
    confusableIds: [],
  },
  {
    id: "sign-halant",
    glyph: "्",
    iast: "",
    hint: "halant — mutes the inherent 'a', joining consonants",
    kind: "sign",
    audioText: "्",
    exampleWordIds: [],
    confusableIds: [],
  },
];

const numerals: Letter[] = [
  {
    id: "num-0",
    glyph: "०",
    iast: "0",
    hint: "shunya — the numeral zero",
    kind: "numeral",
    audioText: "०",
    exampleWordIds: [],
    confusableIds: [],
  },
  {
    id: "num-1",
    glyph: "१",
    iast: "1",
    hint: "ek — the numeral one",
    kind: "numeral",
    audioText: "१",
    exampleWordIds: [],
    confusableIds: [],
  },
  {
    id: "num-2",
    glyph: "२",
    iast: "2",
    hint: "do — the numeral two",
    kind: "numeral",
    audioText: "२",
    exampleWordIds: [],
    confusableIds: [],
  },
  {
    id: "num-3",
    glyph: "३",
    iast: "3",
    hint: "teen — the numeral three",
    kind: "numeral",
    audioText: "३",
    exampleWordIds: [],
    confusableIds: [],
  },
  {
    id: "num-4",
    glyph: "४",
    iast: "4",
    hint: "chaar — the numeral four",
    kind: "numeral",
    audioText: "४",
    exampleWordIds: [],
    confusableIds: [],
  },
  {
    id: "num-5",
    glyph: "५",
    iast: "5",
    hint: "paanch — the numeral five",
    kind: "numeral",
    audioText: "५",
    exampleWordIds: [],
    confusableIds: [],
  },
  {
    id: "num-6",
    glyph: "६",
    iast: "6",
    hint: "chhah — the numeral six",
    kind: "numeral",
    audioText: "६",
    exampleWordIds: [],
    confusableIds: [],
  },
  {
    id: "num-7",
    glyph: "७",
    iast: "7",
    hint: "saat — the numeral seven",
    kind: "numeral",
    audioText: "७",
    exampleWordIds: [],
    confusableIds: [],
  },
  {
    id: "num-8",
    glyph: "८",
    iast: "8",
    hint: "aath — the numeral eight",
    kind: "numeral",
    audioText: "८",
    exampleWordIds: [],
    confusableIds: [],
  },
  {
    id: "num-9",
    glyph: "९",
    iast: "9",
    hint: "nau — the numeral nine",
    kind: "numeral",
    audioText: "९",
    exampleWordIds: [],
    confusableIds: [],
  },
];

const nukta: Letter[] = [
  {
    id: "qa",
    glyph: "क़",
    iast: "qa",
    hint: "q — uvular k (क with nukta), further back than 'k'",
    kind: "nukta",
    audioText: "क़",
    exampleWordIds: ["w-qila"],
    confusableIds: [],
  },
  {
    id: "khha",
    glyph: "ख़",
    iast: "ḵẖa",
    hint: "kh — guttural fricative (ख with nukta), as in 'Bach'",
    kind: "nukta",
    audioText: "ख़",
    exampleWordIds: ["w-khabar"],
    confusableIds: [],
  },
  {
    id: "ghha",
    glyph: "ग़",
    iast: "ġa",
    hint: "gh — voiced guttural (ग with nukta), a rasping 'g'",
    kind: "nukta",
    audioText: "ग़",
    exampleWordIds: ["w-gham"],
    confusableIds: [],
  },
  {
    id: "za",
    glyph: "ज़",
    iast: "za",
    hint: "z — voiced (ज with nukta), as in 'zoo'",
    kind: "nukta",
    audioText: "ज़",
    exampleWordIds: ["w-maza", "w-tez"],
    confusableIds: [],
  },
  {
    id: "fa",
    glyph: "फ़",
    iast: "fa",
    hint: "f — labiodental (फ with nukta), as in 'fun'",
    kind: "nukta",
    audioText: "फ़",
    exampleWordIds: ["w-safar", "w-phone"],
    confusableIds: [],
  },
  {
    id: "rra",
    glyph: "ड़",
    iast: "ṛa",
    hint: "r — retroflex flap (ड with nukta), a flicked 'r'",
    kind: "nukta",
    audioText: "ड़",
    exampleWordIds: ["w-sadak", "w-bada"],
    confusableIds: [],
  },
  {
    id: "rrha",
    glyph: "ढ़",
    iast: "ṛha",
    hint: "rh — aspirated retroflex flap (ढ with nukta)",
    kind: "nukta",
    audioText: "ढ़",
    exampleWordIds: ["w-padhna"],
    confusableIds: [],
  },
];

const conjuncts: Letter[] = [
  {
    id: "conj-ksa",
    glyph: "क्ष",
    iast: "kṣa",
    hint: "ksh — क + ष joined, as in 'kshama'",
    kind: "conjunct",
    audioText: "क्ष",
    exampleWordIds: ["w-kshama", "w-rakshak"],
    confusableIds: [],
  },
  {
    id: "conj-tra",
    glyph: "त्र",
    iast: "tra",
    hint: "tr — त + र joined, as in 'tribhuj'",
    kind: "conjunct",
    audioText: "त्र",
    exampleWordIds: ["w-chhatra", "w-mitra"],
    confusableIds: [],
  },
  {
    id: "conj-jna",
    glyph: "ज्ञ",
    iast: "jña",
    hint: "gy — ज + ञ joined, pronounced 'gya' in Hindi",
    kind: "conjunct",
    audioText: "ज्ञ",
    exampleWordIds: ["w-gyan"],
    confusableIds: [],
  },
  {
    id: "conj-shra",
    glyph: "श्र",
    iast: "śra",
    hint: "shr — श + र joined, as in 'shraddha'",
    kind: "conjunct",
    audioText: "श्र",
    exampleWordIds: ["w-shram"],
    confusableIds: [],
  },
];

/** The complete, ordered letter inventory. */
export const letters: Letter[] = [
  ...vowels,
  ...consonants,
  ...matras,
  ...signs,
  ...numerals,
  ...nukta,
  ...conjuncts,
];
